import { adjustLabelForOptionality } from './option-label'
import React, { useEffect, useRef, useState } from 'react'
import type { ProductOptionWithNameProps } from '../../../../../shared/js/Module'
import type { ChangeEvent, ReactNode } from 'react'

const PATTERN_SIZE_REGEX = /.*\{(\d+)\}$/

const Input = ({
  details,
  disabled,
  id,
  fontClassName,
  label,
  name,
  onChange,
  required,
  restrictionMax,
  restrictionMin,
  triggerFocus,
  values: [value],
  valid,
}: ProductOptionWithNameProps) => {
  const inputId = `input-${id}`
  const descriptionId = details ? `description-input-${id}` : null
  const ariaDescribedBy = descriptionId
    ? { 'aria-describedby': descriptionId }
    : {}
  const fieldRef = useRef<HTMLInputElement>(null)

  const { fontPreview, pattern } = value

  // Error message can be overriden when pattern regex does not match
  const [message, setMessage] = useState(
    valid ? null : 'Vous devez renseigner un texte'
  )

  // Force requirement for input with pattern
  const extraProps: { [key: string]: any } = pattern
    ? { pattern, required: true }
    : {}
  if (pattern) {
    required = true
    const matches = pattern.match(PATTERN_SIZE_REGEX)
    if (matches && matches.length > 0) {
      const size = Number(matches[1])
      extraProps['minLength'] = size
      extraProps['maxLength'] = size
    }
  }
  // Setup or override computed max/min lengths
  if (restrictionMax > 0) {
    extraProps['maxLength'] = restrictionMax
  }
  if (restrictionMin > 0) {
    extraProps['minLength'] = restrictionMin
  }

  // When autofocus is expected, trigger it,
  // then move smoothly into view
  useEffect(() => {
    if (fieldRef.current && triggerFocus) {
      // Do not scroll to field but to its parent otherwise
      // its label won't always be visible.
      fieldRef.current.parentElement?.scrollIntoView({ behavior: 'smooth' })
      fieldRef.current.focus({ preventScroll: true })
    }
  }, [triggerFocus, fontClassName])

  let finalLabel: ReactNode = label
  if (!required) {
    finalLabel = adjustLabelForOptionality(label)
  }

  return (
    <div className='u-mb-2'>
      <label
        className='c-label c-label--image'
        htmlFor={inputId}
        {...ariaDescribedBy}
      >
        {finalLabel}
      </label>
      <input
        aria-required={required}
        aria-invalid={!valid}
        className={fontPreview && fontClassName ? fontClassName : undefined}
        defaultValue={value?.text || ''}
        disabled={disabled}
        id={inputId}
        name={name}
        onChange={handleChange}
        ref={fieldRef}
        type='text'
        onBlur={checkPatternValidity}
        {...extraProps}
      />
      {message && <p className='c-message c-message--help'>{message}</p>}
      {descriptionId && (
        <p id={descriptionId} className='c-message c-message--help'>
          {details}
        </p>
      )}
    </div>
  )

  function handleChange({
    target: { value: text },
  }: ChangeEvent<HTMLInputElement>) {
    setMessage(null)
    onChange?.(id, [{ ...value, text: text.trim(), selected: text.length > 0 }])
  }

  function checkPatternValidity() {
    if (!pattern || !value?.text) return

    // When pattern doesn't match actual value, print a message
    const regex = new RegExp(pattern)
    if (!regex.test(value.text)) {
      let size = 0
      const matches = pattern.match(PATTERN_SIZE_REGEX)
      if (matches && matches.length > 0) {
        size = Number(matches[1])
      }
      if (size > 0 && value.text.length < size) {
        setMessage(
          `Attention, votre saisie n’est pas correcte (${size} caractères attendus)`
        )
      } else {
        setMessage('Attention, votre saisie n’est pas correcte')
      }
    }
  }
}

export default Input
